import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  listCharacterTypes,
  createCharacterType,
  updateCharacterType,
  deleteCharacterType,
  importImageFromUrl,
  listEnhancementTypes,
  getCharacterTypeEnhancements,
  createCharacterTypeEnhancement,
  updateCharacterTypeEnhancement,
  deleteCharacterTypeEnhancement,
  CharacterType,
  CharacterTypeStatus,
  EnhancementType,
  CharacterTypeEnhancement,
} from '../../api/admin';
import AvatarPreview from '../../components/AvatarPreview';
import './Admin.css';

const STATUS_OPTIONS: { value: CharacterTypeStatus; label: string }[] = [
  { value: 'starter', label: 'Starter' },
  { value: 'store', label: 'Store' },
  { value: 'disabled', label: 'Disabled' },
];

type TabType = 'details' | 'enhancements';

const CharacterTypes: React.FC = () => {
  const [types, setTypes] = useState<CharacterType[]>([]);
  const [enhancementTypes, setEnhancementTypes] = useState<EnhancementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; type?: CharacterType } | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const modalOverlayRef = useRef<HTMLDivElement>(null);

  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarBase, setAvatarBase] = useState(1);
  const [status, setStatus] = useState<CharacterTypeStatus>('disabled');
  const [price, setPrice] = useState<number | ''>('');
  // Import states
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);

  // Enhancement states
  const [enhancements, setEnhancements] = useState<CharacterTypeEnhancement[]>([]);
  const [loadingEnhancements, setLoadingEnhancements] = useState(false);
  const [enhancementModal, setEnhancementModal] = useState<{
    mode: 'create' | 'edit';
    enhancement?: CharacterTypeEnhancement;
  } | null>(null);
  const [deleteEnhancementConfirm, setDeleteEnhancementConfirm] = useState<string | null>(null);

  // Enhancement form states
  const [enhLevel, setEnhLevel] = useState(2);
  const [enhTypeId, setEnhTypeId] = useState<string>('');
  const [enhName, setEnhName] = useState('');
  const [enhAddPrompt, setEnhAddPrompt] = useState('');
  const [enhPreviewDesc, setEnhPreviewDesc] = useState('');

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [typesData, enhTypesData] = await Promise.all([
        listCharacterTypes(),
        listEnhancementTypes(),
      ]);
      typesData.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
      setTypes(typesData);
      setEnhancementTypes(enhTypesData);
    } catch (err) {
      setError('Failed to load character types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  // Focus modal overlay once on open (for Escape key)
  useEffect(() => {
    if (modal) {
      modalOverlayRef.current?.focus();
    }
  }, [modal]);

  const fetchEnhancements = useCallback(async (typeId: string) => {
    setLoadingEnhancements(true);
    try {
      const data = await getCharacterTypeEnhancements(typeId);
      setEnhancements(data);
    } catch (err) {
      console.error('Failed to load enhancements:', err);
      setEnhancements([]);
    } finally {
      setLoadingEnhancements(false);
    }
  }, []);

  const openModal = (mode: 'create' | 'edit', type?: CharacterType) => {
    setModal({ mode, type });
    setActiveTab('details');
    setName(type?.name || '');
    setDescription(type?.description || '');
    setAvatarUrl(type?.avatar_url || '');
    setAvatarBase(type?.avatar_base || 1);
    setStatus(type?.status || 'disabled');
    setPrice(type?.price ?? '');
    setImportUrl('');
    setError(null);
    setEnhancements([]);

    if (mode === 'edit' && type) {
      fetchEnhancements(type.id);
    }
  };

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    setError(null);
    try {
      const result = await importImageFromUrl(importUrl);
      if (result.success && result.local_path) {
        setAvatarUrl(result.local_path);
        setImportUrl('');
      } else {
        setError(result.error || 'Failed to import image');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to import image');
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim() || !avatarUrl.trim()) return;

    if (status === 'store' && (price === '' || price <= 0)) {
      setError('Price is required for store character types');
      return;
    }
    if (status === 'starter' && price !== '' && typeof price === 'number' && price <= 0) {
      setError('Price must be positive');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        avatar_url: avatarUrl.trim(),
        avatar_base: avatarBase,
        status,
        price: (status === 'store' || status === 'starter') && typeof price === 'number' ? price : undefined,
      };

      if (modal?.mode === 'create') {
        await createCharacterType(payload);
      } else if (modal?.type) {
        await updateCharacterType(modal.type.id, payload);
      }
      await fetchTypes();
      setModal(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save character type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm === null) return;
    setSaving(true);
    try {
      await deleteCharacterType(deleteConfirm);
      await fetchTypes();
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete character type');
      setDeleteConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  // Enhancement handlers
  const openEnhancementModal = (mode: 'create' | 'edit', enhancement?: CharacterTypeEnhancement) => {
    setEnhancementModal({ mode, enhancement });
    setEnhLevel(enhancement?.level || 2);
    setEnhTypeId(enhancement?.type_id || '');
    setEnhName(enhancement?.name || '');
    setEnhAddPrompt(enhancement?.add_prompt || '');
    setEnhPreviewDesc(enhancement?.preview_description || '');
    setError(null);
  };

  const handleSaveEnhancement = async () => {
    if (!modal?.type || !enhName.trim() || !enhAddPrompt.trim() || enhTypeId === '') return;

    setSaving(true);
    setError(null);
    try {
      const payload = {
        level: enhLevel,
        type_id: enhTypeId,
        name: enhName.trim(),
        add_prompt: enhAddPrompt.trim(),
        preview_description: enhPreviewDesc.trim() || undefined,
      };

      if (enhancementModal?.mode === 'create') {
        await createCharacterTypeEnhancement(modal.type.id, payload);
      } else if (enhancementModal?.enhancement) {
        await updateCharacterTypeEnhancement(enhancementModal.enhancement.id, payload);
      }
      await fetchEnhancements(modal.type.id);
      setEnhancementModal(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save enhancement');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEnhancement = async () => {
    if (deleteEnhancementConfirm === null || !modal?.type) return;
    setSaving(true);
    try {
      await deleteCharacterTypeEnhancement(deleteEnhancementConfirm);
      await fetchEnhancements(modal.type.id);
      setDeleteEnhancementConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete enhancement');
      setDeleteEnhancementConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadgeClass = (status: CharacterTypeStatus) => {
    switch (status) {
      case 'starter':
        return 'status-badge status-starter';
      case 'store':
        return 'status-badge status-store';
      case 'disabled':
        return 'status-badge status-disabled';
      default:
        return 'status-badge';
    }
  };

  // Group enhancements by level
  const enhancementsByLevel = enhancements.reduce((acc, enh) => {
    if (!acc[enh.level]) acc[enh.level] = [];
    acc[enh.level].push(enh);
    return acc;
  }, {} as Record<number, CharacterTypeEnhancement[]>);

  if (loading) {
    return <div className="admin-empty"><p>Loading...</p></div>;
  }

  return (
    <div>
      <div className="admin-page-header">
        <h2>Character Types</h2>
        <button
          className="admin-btn admin-btn-primary"
          onClick={() => openModal('create')}
        >
          Add Type
        </button>
      </div>

      {error && !modal && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-alert admin-alert-info" style={{ marginBottom: '20px' }}>
        Character types define the base templates for characters. Starter types are free for new characters, Store types can be purchased, and Disabled types are hidden.
      </div>

      {types.length === 0 ? (
        <div className="admin-empty">
          <p>No character types yet</p>
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => openModal('create')}
          >
            Add Type
          </button>
        </div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>Avatar</th>
                <th>Name</th>
                <th>Status</th>
                <th>Price</th>
                <th>Enhancements</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((type) => (
                <tr key={type.id}>
                  <td>
                    <AvatarPreview src={type.avatar_url} alt={type.name} />
                  </td>
                  <td>
                    <strong>{type.name}</strong>
                    {type.description && (
                      <div style={{ fontSize: '0.85em', color: '#666' }}>{type.description}</div>
                    )}
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(type.status)}>
                      {type.status}
                    </span>
                  </td>
                  <td>{type.price ? `${type.price} coins` : '-'}</td>
                  <td>{type.enhancement_count ?? '-'}</td>
                  <td>
                    <div className="admin-btn-group">
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-small"
                        onClick={() => openModal('edit', type)}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-btn admin-btn-danger admin-btn-small"
                        onClick={() => setDeleteConfirm(type.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modal && (
        <div className="admin-modal-overlay" onKeyDown={(e) => { if (e.key === 'Escape') setModal(null); }} tabIndex={-1} ref={modalOverlayRef}>
          <div className="admin-modal" style={{ maxWidth: '600px' }}>
            <h3>{modal.mode === 'create' ? 'Add Character Type' : `Edit: ${modal.type?.name}`}</h3>

            {error && <div className="admin-alert admin-alert-error">{error}</div>}

            {/* Tabs - only show for edit mode */}
            {modal.mode === 'edit' && (
              <div className="admin-tabs" style={{ marginBottom: '16px' }}>
                <button
                  className={`admin-tab ${activeTab === 'details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('details')}
                >
                  Details
                </button>
                <button
                  className={`admin-tab ${activeTab === 'enhancements' ? 'active' : ''}`}
                  onClick={() => setActiveTab('enhancements')}
                >
                  Enhancements
                </button>
              </div>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <>
                <div className="admin-form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Ninja, Robot, Wizard"
                    maxLength={50}
                  />
                </div>

                <div className="admin-form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="A brief description of this character type"
                    maxLength={200}
                  />
                </div>

                <div className="admin-form-group">
                  <label>Avatar URL</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={avatarUrl}
                    onChange={(e) => setAvatarUrl(e.target.value)}
                    placeholder="/avatars/store/ninja.png"
                  />
                  {avatarUrl && (
                    <div style={{ marginTop: '8px' }}>
                      <img
                        src={avatarUrl}
                        alt="Preview"
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>

                <div className="admin-form-group">
                  <label>Avatar Base Number</label>
                  <input
                    type="number"
                    className="admin-form-input"
                    value={avatarBase}
                    onChange={(e) => setAvatarBase(parseInt(e.target.value) || 1)}
                    min={1}
                    max={10}
                  />
                  <small style={{ color: '#666', fontSize: '0.85em' }}>
                    The base number for this character type (1-10)
                  </small>
                </div>

                <div className="admin-form-group">
                  <label>Import from URL</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      className="admin-form-input"
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="https://example.com/image.png"
                      style={{ flex: 1 }}
                    />
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={handleImport}
                      disabled={importing || !importUrl.trim()}
                    >
                      {importing ? 'Importing...' : 'Import'}
                    </button>
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>Status</label>
                  <select
                    className="admin-form-input"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as CharacterTypeStatus)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {(status === 'store' || status === 'starter') && (
                  <div className="admin-form-group">
                    <label>Price (coins)</label>
                    <input
                      type="number"
                      className="admin-form-input"
                      value={price}
                      onChange={(e) => setPrice(e.target.value ? parseInt(e.target.value) : '')}
                      min={1}
                      max={10000}
                    />
                    {status === 'starter' && (
                      <small style={{ color: '#666', fontSize: '0.85em' }}>
                        Starters with a price will also appear in the store for purchase
                      </small>
                    )}
                  </div>
                )}

                <div className="admin-modal-actions">
                  <button className="admin-btn admin-btn-secondary" onClick={() => setModal(null)}>
                    Cancel
                  </button>
                  <button
                    className="admin-btn admin-btn-primary"
                    onClick={handleSave}
                    disabled={saving || !name.trim() || !avatarUrl.trim()}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </>
            )}

            {/* Enhancements Tab */}
            {activeTab === 'enhancements' && modal.mode === 'edit' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <p style={{ margin: 0, color: '#666' }}>
                    Define enhancement options available at each level for this character type.
                  </p>
                  <button
                    className="admin-btn admin-btn-primary admin-btn-small"
                    onClick={() => openEnhancementModal('create')}
                  >
                    Add Option
                  </button>
                </div>

                {loadingEnhancements ? (
                  <p>Loading enhancements...</p>
                ) : enhancements.length === 0 ? (
                  <div className="admin-alert admin-alert-info">
                    No enhancements defined for this character type yet.
                  </div>
                ) : (
                  <div className="enhancements-list">
                    {[2, 3, 4].map((level) => {
                      const levelEnhancements = enhancementsByLevel[level] || [];
                      if (levelEnhancements.length === 0) return null;
                      const isAutoApply = levelEnhancements.length === 1;
                      return (
                        <div key={level} className="level-group" style={{ marginBottom: '16px' }}>
                          <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>
                            Level {level}
                            {isAutoApply && (
                              <span style={{
                                marginLeft: '8px',
                                fontSize: '0.8em',
                                color: '#28a745',
                                fontWeight: 'normal'
                              }}>
                                (Auto-applies)
                              </span>
                            )}
                          </h4>
                          <div style={{ paddingLeft: '16px' }}>
                            {levelEnhancements.map((enh) => (
                              <div
                                key={enh.id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '8px',
                                  marginBottom: '4px',
                                  background: '#f8f9fa',
                                  borderRadius: '4px'
                                }}
                              >
                                <div>
                                  <strong>{enh.name}</strong>
                                  <span style={{ color: '#666', marginLeft: '8px' }}>({enh.type_name})</span>
                                  <div style={{ fontSize: '0.85em', color: '#666' }}>
                                    {enh.add_prompt}
                                  </div>
                                </div>
                                <div className="admin-btn-group">
                                  <button
                                    className="admin-btn admin-btn-secondary admin-btn-small"
                                    onClick={() => openEnhancementModal('edit', enh)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="admin-btn admin-btn-danger admin-btn-small"
                                    onClick={() => setDeleteEnhancementConfirm(enh.id)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="admin-modal-actions">
                  <button className="admin-btn admin-btn-secondary" onClick={() => setModal(null)}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Enhancement Modal */}
      {enhancementModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" style={{ maxWidth: '450px' }}>
            <h3>{enhancementModal.mode === 'create' ? 'Add Enhancement Option' : 'Edit Enhancement Option'}</h3>

            {error && <div className="admin-alert admin-alert-error">{error}</div>}

            <div className="admin-form-group">
              <label>Level</label>
              <select
                className="admin-form-input"
                value={enhLevel}
                onChange={(e) => setEnhLevel(parseInt(e.target.value))}
              >
                <option value={2}>Level 2</option>
                <option value={3}>Level 3</option>
                <option value={4}>Level 4</option>
              </select>
            </div>

            <div className="admin-form-group">
              <label>Type</label>
              <select
                className="admin-form-input"
                value={enhTypeId}
                onChange={(e) => setEnhTypeId(e.target.value)}
              >
                <option value="">Select type...</option>
                {enhancementTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.display_name}</option>
                ))}
              </select>
            </div>

            <div className="admin-form-group">
              <label>Name</label>
              <input
                type="text"
                className="admin-form-input"
                value={enhName}
                onChange={(e) => setEnhName(e.target.value)}
                placeholder="e.g., Blue Ninja Outfit"
                maxLength={50}
              />
            </div>

            <div className="admin-form-group">
              <label>Add Prompt</label>
              <input
                type="text"
                className="admin-form-input"
                value={enhAddPrompt}
                onChange={(e) => setEnhAddPrompt(e.target.value)}
                placeholder="e.g., wearing a blue ninja gi"
                maxLength={200}
              />
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                Describe what the character should be wearing/holding
              </small>
            </div>

            <div className="admin-form-group">
              <label>Preview Description (optional)</label>
              <input
                type="text"
                className="admin-form-input"
                value={enhPreviewDesc}
                onChange={(e) => setEnhPreviewDesc(e.target.value)}
                placeholder="A traditional blue ninja outfit..."
                maxLength={200}
              />
            </div>

            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setEnhancementModal(null)}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSaveEnhancement}
                disabled={saving || !enhName.trim() || !enhAddPrompt.trim() || enhTypeId === ''}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Character Type Confirmation */}
      {deleteConfirm !== null && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>Confirm Delete</h3>
            <p className="confirm-dialog-text">
              Are you sure you want to delete this character type?
              This cannot be undone.
            </p>
            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Enhancement Confirmation */}
      {deleteEnhancementConfirm !== null && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>Confirm Delete Enhancement</h3>
            <p className="confirm-dialog-text">
              Are you sure you want to delete this enhancement option?
            </p>
            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-secondary" onClick={() => setDeleteEnhancementConfirm(null)}>
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={handleDeleteEnhancement}
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .status-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.85em;
          font-weight: 500;
          text-transform: capitalize;
        }
        .status-starter {
          background: #d4edda;
          color: #155724;
        }
        .status-store {
          background: #cce5ff;
          color: #004085;
        }
        .status-disabled {
          background: #f8d7da;
          color: #721c24;
        }
        .admin-tabs {
          display: flex;
          border-bottom: 2px solid #e9ecef;
        }
        .admin-tab {
          padding: 8px 16px;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 1em;
          color: #666;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
        }
        .admin-tab:hover {
          color: #333;
        }
        .admin-tab.active {
          color: #007bff;
          border-bottom-color: #007bff;
        }
      `}</style>
    </div>
  );
};

export default CharacterTypes;
